import React from 'react';
import { Card, CardBody, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import classnames from 'classnames';
import CompanyEdit from '../companies/company-edit';
import BillingTab from './billing-tab';
import UsageTab from './usage-tab';
import LoginAccountsTab from './login-accounts-tab';
import { COMPANY_SUSPENSION_EVENT } from '../../utils/session';
import { isCompanySuspended } from '../../layouts/layout-components/suspension/suspension-banner';
import { isPrivateAccount } from '../../utils/accountType';

const TABS = [
	{ id: 'company-settings', label: 'Company settings' },
	{ id: 'billing', label: 'Billing' },
	{ id: 'usage', label: 'Usage' },
	{ id: 'login-accounts', label: 'Login Accounts' },
];

// The tab's old name in a saved address (?tab=admins)
const OLD_TAB_IDS = { admins: 'login-accounts' };

// A Private account (utils/accountType.js): "Your details" in place of the company settings, and no Login Accounts tab
// (one person runs it)
function tabsFor(isPrivate) {
	if (!isPrivate) {
		return TABS;
	}
	return TABS.filter((tab) => tab.id !== 'login-accounts').map((tab) => tab.id === 'company-settings' ? { id: tab.id, label: 'Your details' } : tab);
}

// The only tab a suspended company's admin can use (to pay and lift the suspension)
const SUSPENDED_TAB = 'billing';

class AccountPage extends React.Component {
	constructor(props) {
		super(props);
		const asked = new URLSearchParams((props.location && props.location.search) || '').get('tab');
		const fromUrl = OLD_TAB_IDS[asked] || asked;
		const suspended = isCompanySuspended();
		this.state = {
			activeTab: suspended ? SUSPENDED_TAB : (tabsFor(isPrivateAccount()).some((tab) => tab.id === fromUrl) ? fromUrl : 'company-settings'),
			suspended: suspended
		};
		this.toggleTab = this.toggleTab.bind(this);
		this.onSuspensionChange = this.onSuspensionChange.bind(this);
	}

	componentDidMount() {
		window.addEventListener(COMPANY_SUSPENSION_EVENT, this.onSuspensionChange);
	}

	componentWillUnmount() {
		window.removeEventListener(COMPANY_SUSPENSION_EVENT, this.onSuspensionChange);
	}

	// Suspended: back to Billing with the other tabs disabled; reactivated: every tab works again
	onSuspensionChange() {
		const suspended = isCompanySuspended();
		this.setState((state) => ({
			suspended: suspended,
			activeTab: suspended ? SUSPENDED_TAB : state.activeTab
		}));
		if (suspended) {
			this.rememberTab(SUSPENDED_TAB);
		}
	}

	toggleTab(tab) {
		if (this.state.suspended && tab !== SUSPENDED_TAB) {
			return;
		}
		if (this.state.activeTab !== tab) {
			this.setState({ activeTab: tab });
			this.rememberTab(tab);
		}
	}

	// The open tab in the address (?tab=billing), so a refresh (F5) stays on it
	rememberTab(tab) {
		const { history, location } = this.props;
		if (!history || !location) {
			return;
		}
		const params = new URLSearchParams(location.search || '');
		if (params.get('tab') === tab) {
			return;
		}
		params.set('tab', tab);
		history.replace({ pathname: location.pathname, search: '?' + params.toString() });
	}

	render() {
		const { activeTab, suspended } = this.state;
		return (
			<Card>
				<CardBody>
					<Nav tabs>
						{tabsFor(isPrivateAccount()).map((tab) => {
							const disabled = suspended && tab.id !== SUSPENDED_TAB;
							return (
								<NavItem key={tab.id} style={disabled ? { cursor: 'not-allowed' } : undefined}>
									<NavLink
										className={classnames({ active: activeTab === tab.id })}
										disabled={disabled}
										aria-disabled={disabled}
										style={disabled ? { color: '#adb5bd', pointerEvents: 'none' } : { cursor: 'pointer' }}
										onClick={() => this.toggleTab(tab.id)}
									>
										{tab.label}
									</NavLink>
								</NavItem>
							);
						})}
					</Nav>
					<TabContent activeTab={activeTab}>
						<TabPane tabId="company-settings">
							{!suspended && <CompanyEdit />}
						</TabPane>
						<TabPane tabId="billing">
							{activeTab === 'billing' && <BillingTab />}
						</TabPane>
						<TabPane tabId="usage">
							{activeTab === 'usage' && <UsageTab />}
						</TabPane>
						<TabPane tabId="login-accounts">
							{activeTab === 'login-accounts' && !isPrivateAccount() && <LoginAccountsTab />}
						</TabPane>
					</TabContent>
				</CardBody>
			</Card>
		);
	}
}

export default AccountPage;
